import { useMemo } from 'react'
import { BoardView } from '@/components/kanban/board-view'
import { IssueDataTable, TableSkeleton } from '@/components/tables/data-table'
import { ISSUE_COLUMNS } from '@/components/tables/issue-columns'
import { ViewModeToggle } from '@/components/common/view-mode-toggle'
import { useViewMode } from '@/hooks/use-view-mode'
import { ViewError } from '@/components/common/query-states'
import { ViewHeader } from '@/components/shell/view-header'
import { useOpenIssueSheet } from '@/hooks/use-ui-state'
import { useViewFilters } from '@/hooks/use-view-filters'
import { useContextOrder, useReorderContext } from '@/hooks/use-context-order'
import { useMe } from '@/hooks/use-me'
import { useIssues } from '@/lib/query'
import { useSetIssueField } from '@/lib/mutations'
import { byManualOrder, EMPTY_ORDER } from '@/lib/context-order'
import { columnTotal } from '@/lib/grouping'
import { PRIORITY_ORDER, priorityVar } from '@/lib/stage'
import { BACKLOG_VIEW } from '@/lib/views'
import type { WorkIssue } from '@/types/github'

// Columns are the live Priority options. The pool is the unassigned set:
// stage Backlog (Priority=Backlog) + stage Ready (P1–P3) — dragging across
// writes Priority and the work repo's stage-sync derives the stage. Within-band
// order is the team's own backlog ordering (orders/backlog.json), independent of
// anyone's personal order — not the old shared global rank. See n3oltd/work#771.
export default function Backlog() {
  const query = useIssues()
  const openIssue = useOpenIssueSheet()
  const chipFilter = useViewFilters()
  const me = useMe()
  const setField = useSetIssueField()
  const [view, setView] = useViewMode()
  const order = useContextOrder('backlog').data ?? EMPTY_ORDER
  const reorder = useReorderContext('backlog')

  const itemsByColumn = useMemo(() => {
    const pool = chipFilter.apply(BACKLOG_VIEW.pool(query.data ?? [], {}))
    const map = new Map<string, WorkIssue[]>(PRIORITY_ORDER.map((p) => [p, [] as WorkIssue[]]))
    for (const issue of pool) map.get(issue.priority ?? 'Backlog')?.push(issue)
    for (const [band, list] of map) list.sort(byManualOrder(order.groups[band] ?? []))
    return map
  }, [query.data, chipFilter, order])

  // The backlog order is shared, so only the reorder team may change it (the
  // server enforces this; we mirror it in the UI). A reorder also writes the
  // whole band's order, so the visible band must be a complete cohort — grouped
  // by Priority, a priority chip is safe (hides whole columns), any other active
  // chip hides within-column members → sort-only. (Cross-column priority changes
  // stay open to everyone — that's a normal field edit, not the manual order.)
  const cohortComplete = chipFilter.active.every((k) => k === 'priority')
  const canReorder = !!me?.canReorderOthers && cohortComplete

  const hint = !me?.canReorderOthers
    ? 'within-band order is set by the planning team (you can still change Priority)'
    : cohortComplete
      ? 'drag within a column to set the backlog order'
      : 'filtered — clear filters to reorder'

  return (
    <div className="flex h-full flex-col">
      <ViewHeader
        title="Backlog"
        meta={
          view === 'table'
            ? `${columnTotal(itemsByColumn)} items`
            : `${columnTotal(itemsByColumn)} items · grouped by Priority · ${hint}`
        }
        toolbar={<ViewModeToggle mode={view} onChange={setView} />}
      />
      <div className="min-h-0 flex-1">
        {view === 'table' ? (
          query.isError ? (
            <ViewError what="the backlog" error={query.error} onRetry={() => query.refetch()} />
          ) : query.isPending ? (
            <TableSkeleton />
          ) : (
            <IssueDataTable
              issues={[...itemsByColumn.values()].flat()}
              columns={ISSUE_COLUMNS}
              sortable
              bulkKind="work"
              emptyTitle="Backlog is empty"
            />
          )
        ) : (
          <BoardView
            query={query}
            skeletonColumns={4}
            columns={PRIORITY_ORDER.map((p) => ({
              key: p,
              title: p,
              glyph: (
                <span
                  className="size-2 rounded-full"
                  style={{ background: `var(${priorityVar(p)})` }}
                  aria-hidden
                />
              ),
            }))}
            itemsByColumn={itemsByColumn}
            showEffort
            excludeContext="backlog"
            onCardClick={openIssue}
            onMoveAcross={(issue, pos) => {
              setField.mutate({ issue, fieldName: 'Priority', value: pos.toColumn })
              // Also persist the drop position in the new band's order, else the
              // card lands at the unplaced tail instead of where it was dropped.
              if (canReorder) reorder(pos.toColumn, pos.orderedIds)
            }}
            onReorder={canReorder ? (_issue, pos) => reorder(pos.toColumn, pos.orderedIds) : undefined}
          />
        )}
      </div>
    </div>
  )
}
